import { isMap, set } from 'lodash';
import { ITranslationKey } from '../../ITranslationKey';

export enum TranslationSchemaType {
  Widget = 'widget',
  Object = 'object',
  KeyedObject = 'keyed-object',
  Array = 'array',
  String = 'string',
}

export interface ITranslationUpdateContext {
  /** Valore sul quale è possibile applicare il `path` per effettuare la sostituzione della traduzione */
  root: any;
  /** Traduzioni disponibili, trasformata in una Map */
  translations: Map<string, string>;
  /**
   * Imposta il valore della traduzione sulla root.
   * Se la traduzione non è presente, viene ignorata.
   */
  set: (path: string, value: string | undefined) => void;
}

export abstract class BaseTranslationSchema {
  shouldOmitKey?: boolean;

  abstract type: TranslationSchemaType;
  abstract extract(value: any, path: string, prefix: string): ITranslationKey[];

  /**
   * Versione interna di translate che opera ricorsivamente sull'oggetto
   * da tradurre.
   */
  protected abstract translateValue(
    value: any,
    path: string,
    prefix: string,
    ctx: ITranslationUpdateContext,
  ): void;

  /**
   * Applica le traduzioni al valore passato, facendo side-effect sull'oggetto.
   *
   * @param translationKeys Array di chiavi di traduzione. Può essere passato
   *  anche un oggetto Map per ottimizzare le performance ed evitare di ricreare
   *  la Map ad ogni chiamata.
   */
  translate<T extends object>(
    root: T,
    translationKeys: ITranslationKey[] | Map<string, string>,
  ): T {
    const translations = isMap(translationKeys)
      ? translationKeys
      : new Map(translationKeys.map(t => [t.chiave, t.valore]));

    const ctx: ITranslationUpdateContext = {
      root,
      translations,
      set: (path, value) => {
        if (path.startsWith('.')) path = path.slice(1);

        // Se la traduzione non è presente, non viene applicata.
        // Inoltre, se abbiamo già applicato una traduzione, non la
        // sovrascriviamo (in questo caso la Key sarebbe diversa).
        if (!value || value.trim() === '') return;

        set(root, path, value);
      },
    };

    this.translateValue(root, '', '', ctx);
    return root;
  }

  /**
   * Se chiamato, la generazione di questa chiave di traduzione non terrà
   * conto della chiave al nodo corrente.
   * Vale solo all'interno di un Object. Es:
   *
   *  tran.object({
   *    text: tran.string().omitKey() // Salta "text"
   *  })
   */
  omitKey(omit = true) {
    this.shouldOmitKey = omit;
    return this;
  }
}
